// ── 출퇴근 데이터 훅 ──────────────────────────────────────────────────────────
// 데이터 로딩, 필터, 페이지네이션 상태 관리
import { useEffect, useMemo, useState } from 'react';
import { nowKST } from '../../../utils/date';
import { apiClient } from '../../../api/apiClient';
import { PAGE_SIZE, MOBILE_BREAKPOINT, normalizeIp, formatIpWithAlias, matchesAttendanceFilter } from './utils';

/**
 * 출퇴근 데이터 페치 및 필터/페이지네이션 상태 훅
 * @param {object} params
 * @param {string}   params.attendancePath    - 출퇴근 API 경로
 * @param {Function} params.showToast         - 토스트 표시 함수
 * @returns 데이터, 필터 상태, 페이지 상태, fetchAll, computed values
 */
export function useAttendanceData({
  attendancePath, showToast, selectedCompanyCodes, selectedUserId,
  initialYear, initialMonth, // 달력 탭에서 이름을 눌러 왔을 때 그 달로 연다(2026-09-29). 없으면 이번 달.
  initialIncludeResigned = false, // 그때는 '전체'(퇴사자 포함)로 연다 — 달력엔 그달 퇴사(예정)자도 있다.
}) {
  // ── 뷰 필터 상태 ────────────────────────────────────────────────────────────
  // nowKST: utils/date.js 사용 (new Date 직접 사용 금지)
  const now = nowKST();
  const [year,        setYear]        = useState(() => initialYear || now.getUTCFullYear());
  const [month,       setMonth]       = useState(() => initialMonth || (now.getUTCMonth() + 1));
  const [nameQuery,   setNameQuery]   = useState('');
  const [ipFilter,    setIpFilter]    = useState('');
  const [activeStatusFilters, setActiveStatusFilters] = useState([]); // 빠른 필터 (정상/지각/조퇴/퇴근미체크)
  const [includeResigned, setIncludeResigned] = useState(Boolean(initialIncludeResigned)); // 재직중(false)/전체(true) 토글 — 연차와 동일 패턴

  // ── 서버 데이터 상태 ────────────────────────────────────────────────────────
  const [rows,     setRows]     = useState([]); // 출퇴근 행 목록
  const [ips,      setIps]      = useState([]); // 등록된 IP 목록
  const [holidays, setHolidays] = useState(new Set()); // 공공 공휴일 Set (YYYY-MM-DD) — 캘린더 표시용
  // 선택 직원의 그 달 연차/반차 음영 맵 { 'YYYY-MM-DD': 'full_day'|'half_am'|'half_pm' } (캘린더 셀 음영용)
  const [leaveByDate, setLeaveByDate] = useState({});

  // ── 페이지네이션 / UI 상태 ──────────────────────────────────────────────────
  const [currentPage,    setCurrentPage]    = useState(1);
  const [expandedRowId,  setExpandedRowId]  = useState(null);
  const [isMobile,       setIsMobile]       = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);

  // ── 데이터 로드 ─────────────────────────────────────────────────────────────
  async function fetchAll() {
    // 통합 모드(Phase 1-2): selectedCompanyCodes 있으면 codes 쿼리 추가
    const codesQ = Array.isArray(selectedCompanyCodes) && selectedCompanyCodes.length > 0 ? `&codes=${selectedCompanyCodes.join(',')}` : '';
    const resignedQ = includeResigned ? '&include_resigned=true' : '';
    const isIntegratedAttendance = /\/integrated-menus\/\d+\/attendance$/.test(attendancePath);
    const ipsPath = isIntegratedAttendance ? `${attendancePath}/ips` : '/api/admin/ips';
    // 구글시트 URL 조회(/api/admin/sheets/info)는 제거됨(2026-09-01) — 이 화면의 시트 UI 는 2026-04-26 부터
    //   숨김 상태인데 호출만 남아, 고객 테넌트(의도된 403)·미설정 환경(구글 권한 500)에서 매 로드마다
    //   오류 토스트를 띄우던 원인이었다. 연차 관리의 시트 기능은 현역이라 서버 라우트는 유지.
    const [rowRes, ipRes] = await Promise.all([
      apiClient.get(`${attendancePath}?year=${year}&month=${month}${codesQ}${resignedQ}`),
      apiClient.get(ipsPath),
    ]);

    if (rowRes.ok) setRows(rowRes.data || []);
    if (ipRes.ok) setIps(ipRes.data || []);

    if (!rowRes.ok) showToast(rowRes.data?.message || '데이터 조회에 실패했습니다', 'error');
    // IP 목록(/api/admin/ips)은 PINE 전역 데이터라 고객 테넌트는 의도적으로 차단(403)된다. 보조 IP 별칭 필터일
    //   뿐이므로 실패해도 에러 토스트를 띄우지 않고 빈 목록으로 둔다(페이지 정상). 운영자는 200 이라 영향 없음.
  }

  // year/month/회사필터/재직여부 변경 시 자동 재로드
  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, includeResigned, JSON.stringify(selectedCompanyCodes || [])]);

  // 선택 직원의 그 달 연차/반차 — 달력 셀 음영용 (직원 선택/월 변경 시 재조회)
  //   미선택이면 빈 맵. 실패해도 silent (음영만 안 뜸 — 출퇴근 표시엔 영향 없음).
  useEffect(() => {
    if (!selectedUserId) { setLeaveByDate({}); return undefined; }
    let mounted = true;
    apiClient.get(`/api/admin/attendance/leaves?user_id=${selectedUserId}&year=${year}&month=${month}`)
      .then((r) => {
        if (!mounted) return;
        const list = Array.isArray(r.data?.data) ? r.data.data : [];
        const map = {};
        for (const row of list) {
          if (row?.date_ymd && row?.calendar_effect) map[row.date_ymd] = row.calendar_effect;
        }
        setLeaveByDate(map);
      })
      .catch(() => { if (mounted) setLeaveByDate({}); });
    return () => { mounted = false; };
  }, [selectedUserId, year, month]);

  // 공공 공휴일 (해당 연도) — year 변경 시 재조회 (캘린더 셀 빨간 표시용)
  useEffect(() => {
    let mounted = true;
    apiClient.get(`/api/holidays?year=${year}`).then((r) => {
      if (!mounted) return;
      const list = Array.isArray(r.data?.holidays) ? r.data.holidays : [];
      setHolidays(new Set(list));
    }).catch(() => { /* 공휴일 조회 실패는 silent — 캘린더 동작 자체엔 영향 없음 */ });
    return () => { mounted = false; };
  }, [year]);

  // 필터 변경 시 첫 페이지로 이동 + 펼침 상태 초기화
  useEffect(() => {
    setCurrentPage(1);
    setExpandedRowId(null);
  }, [year, month, nameQuery, ipFilter]);

  // 페이지 수 초과 방지
  useEffect(() => {
    setCurrentPage((prev) => Math.min(Math.max(prev, 1), Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE))));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, nameQuery, ipFilter]);

  // 화면 크기 변경 감지
  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── 이름 필터 적용 ──────────────────────────────────────────────────────────
  const nameFilteredRows = useMemo(() => {
    const keyword = String(nameQuery || '').trim();
    if (!keyword) return rows;
    return rows.filter((row) => String(row.name || '').includes(keyword));
  }, [rows, nameQuery]);

  // 이름 필터가 단일 직원을 특정하면 해당 직원 정보 반환 (IP 필터용)
  const selectedUserForIpFilter = useMemo(() => {
    const keyword = String(nameQuery || '').trim();
    if (!keyword) return null;
    const userMap = new Map();
    nameFilteredRows.forEach((row) => {
      const uid = Number(row.user_id);
      if (!Number.isFinite(uid)) return;
      if (!userMap.has(uid)) userMap.set(uid, { user_id: uid, name: row.name || '' });
    });
    if (userMap.size !== 1) return null;
    return [...userMap.values()][0];
  }, [nameFilteredRows, nameQuery]);

  // IP 별칭 드롭다운 옵션 (Phase 1-2 후속)
  // · 이름으로 단일 직원 특정 시: 그 직원 소유 + 공용(NULL) IP만
  // · 이름 미지정/다중 매칭: 등록된 모든 active IP (공용/개인 상관없이 별칭 부여된 것 우선)
  const ipFilterOptions = useMemo(() => {
    const map = new Map();
    const targetUserId = selectedUserForIpFilter ? Number(selectedUserForIpFilter.user_id) : null;
    ips.forEach((ipRow) => {
      if (!ipRow?.is_active) return;
      const owner = ipRow.user_id === null || ipRow.user_id === undefined ? null : Number(ipRow.user_id);
      // 단일 직원 특정 시: 그 직원 + 공용만 / 미특정: 전부
      if (targetUserId !== null && owner !== null && owner !== targetUserId) return;
      const pureIp = normalizeIp(ipRow.ip_address);
      if (!pureIp || pureIp === '-') return;
      // 같은 IP가 여러 행에 있을 수 있음 — 별칭 있는 행 우선, 그 다음 first-write
      const existing = map.get(pureIp);
      const aliasText = String(ipRow.label || '').trim();
      if (existing && !aliasText) return; // 기존이 있고 신규는 별칭 없음 → skip
      map.set(pureIp, { value: pureIp, label: formatIpWithAlias(pureIp, ipRow.label), alias: aliasText });
    });
    return [...map.values()];
  }, [ips, selectedUserForIpFilter]);

  // 현재 선택된 IP 옵션 (IP 별칭 표시용)
  const selectedIpOption = useMemo(
    () => ipFilterOptions.find((option) => option.value === ipFilter) || null,
    [ipFilterOptions, ipFilter]
  );

  // IP 필터 유효성 유지 — 옵션 목록에서 사라진 경우 자동 해제
  useEffect(() => {
    if (ipFilter && !ipFilterOptions.some((option) => option.value === ipFilter)) setIpFilter('');
  }, [ipFilterOptions, ipFilter]);

  // ── 단계별 필터 적용 ─────────────────────────────────────────────────────
  // 1단계: 이름 + IP 필터만 적용 (타입 필터 제외) — 카운트 산출 기준
  const nameIpFilteredRows = useMemo(() => {
    if (!ipFilter) return nameFilteredRows;
    return nameFilteredRows.filter((row) => {
      const inIp  = normalizeIp(row.ip_address);
      const outIp = normalizeIp(row.check_out_ip_address);
      return inIp === ipFilter || outIp === ipFilter;
    });
  }, [nameFilteredRows, ipFilter]);

  // 2단계: + 상태 필터 적용 (표시용)
  const filteredRows = useMemo(() => {
    if (activeStatusFilters.length === 0) return nameIpFilteredRows;
    return nameIpFilteredRows.filter((row) => activeStatusFilters.some((k) => matchesAttendanceFilter(row, k)));
  }, [nameIpFilteredRows, activeStatusFilters]);

  // 빠른 필터 배지 카운트 — nameIpFilteredRows 기준 (타입 필터 제외 → 클릭해도 카운트 변화 없음)
  const statusCounts = useMemo(() => {
    const c = { normal: 0, late: 0, early: 0, missing_out: 0, dayoff: 0 };
    nameIpFilteredRows.forEach((row) => {
      if (matchesAttendanceFilter(row, 'normal'))      c.normal++;
      if (matchesAttendanceFilter(row, 'late'))        c.late++;
      if (matchesAttendanceFilter(row, 'early'))       c.early++;
      if (matchesAttendanceFilter(row, 'missing_out')) c.missing_out++;
      if (matchesAttendanceFilter(row, 'dayoff'))      c.dayoff++;
    });
    return c;
  }, [nameIpFilteredRows]);

  // 직원 선택 시 그 직원 카운트 (이름+IP 필터, 타입 필터 제외 — 클릭해도 카운트 유지). 우선순위 1회 분류
  const userUnfilteredCounts = useMemo(() => {
    if (!selectedUserId) return null;
    const c = { normal: 0, late: 0, early: 0, missing_out: 0, dayoff: 0 };
    nameIpFilteredRows.filter((r) => Number(r.user_id) === Number(selectedUserId)).forEach((row) => {
      const k = ['dayoff', 'missing_out', 'late', 'early', 'normal'].find((key) => matchesAttendanceFilter(row, key));
      if (k) c[k] += 1;
    });
    return c;
  }, [selectedUserId, nameIpFilteredRows]);

  // ── 직원별 그룹핑 (모바일 아코디언/바 시각화용 — Phase 1-2 B) ───────────────
  // filteredRows(이름+IP+상태 필터 적용 후) 기준 → 한 직원 카드 안에서 보일 행과 동일
  // counts는 우선순위 1회 분류 (dayoff > missing_out > late > early > normal)로
  // 합계가 totalDays(=workDays+offDays)와 일치 → 바 시각화 100% 충전 가능
  const groupedByUser = useMemo(() => {
    const map = {};
    filteredRows.forEach((row) => {
      const uid = Number(row.user_id);
      if (!Number.isFinite(uid)) return;
      if (!map[uid]) {
        map[uid] = {
          user: {
            id: uid,
            name: row.name || '',
            department: row.department || '',
            company_code: row.company_code || '',
            company_name: row.company_name || '',
          },
          counts: { normal: 0, late: 0, early: 0, missing_out: 0, dayoff: 0 },
          workDays: 0,
          offDays: 0,
          rows: [],
        };
      }
      const g = map[uid];
      g.rows.push(row);
      // 우선순위 분류: 휴무 → 퇴근미체크 → 지각 → 조퇴 → 정상 (각 행 1회만 카운트)
      if (matchesAttendanceFilter(row, 'dayoff'))           { g.counts.dayoff++;      g.offDays++; }
      else if (matchesAttendanceFilter(row, 'missing_out')) { g.counts.missing_out++; g.workDays++; }
      else if (matchesAttendanceFilter(row, 'late'))        { g.counts.late++;        g.workDays++; }
      else if (matchesAttendanceFilter(row, 'early'))       { g.counts.early++;       g.workDays++; }
      else if (matchesAttendanceFilter(row, 'normal'))      { g.counts.normal++;      g.workDays++; }
      else                                                  {                          g.workDays++; }
    });
    // 직원별 행은 날짜 오름차순 정렬 (모바일 펼침 시 자연스러운 순서)
    Object.values(map).forEach((g) => {
      g.rows.sort((a, b) => String(a.date_ymd || a.date || '').localeCompare(String(b.date_ymd || b.date || '')));
    });
    return map;
  }, [filteredRows]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows  = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, currentPage]);

  return {
    year, setYear, month, setMonth,
    nameQuery, setNameQuery, ipFilter, setIpFilter,
    activeStatusFilters, setActiveStatusFilters, statusCounts, userUnfilteredCounts,
    includeResigned, setIncludeResigned, // 재직중/전체 토글 (Phase 1-2 후속)
    rows, ips, holidays, leaveByDate,
    currentPage, setCurrentPage, expandedRowId, setExpandedRowId,
    isMobile,
    filteredRows, pagedRows, totalPages,
    nameIpFilteredRows, // 카운트 산출용 (타입 필터 제외)
    groupedByUser, // 모바일 직원별 카드 (Phase 1-2 B)
    selectedUserForIpFilter, ipFilterOptions, selectedIpOption,
    fetchAll,
  };
}
